<template>
    <div ref="container">
        <div v-if="(useMobile || forceMobile) && (showMobileFilters || mobileSortableHeaders.length)" class="flex gap-3 mb-4">
            <button
                v-if="showMobileFilters"
                class="flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-black bg-white px-3 py-3 text-sm font-semibold text-gray-900"
                @click="openMobileFilters"
            >
                <svg aria-hidden="true" class="h-6 w-6 shrink-0 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" d="M4 6h16M4 12h16M4 18h16" />
                    <circle cx="8" cy="6" r="2" fill="white" />
                    <circle cx="16" cy="12" r="2" fill="white" />
                    <circle cx="10" cy="18" r="2" fill="white" />
                </svg>
                {{ $t('ui.all_filters') }}
            </button>
            <button
                v-if="mobileSortableHeaders.length"
                class="flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-black bg-white px-3 py-3 text-sm font-semibold text-gray-900"
                @click="openMobileSort"
            >
                <svg aria-hidden="true" class="h-6 w-6 shrink-0 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7 4v16m0 0-4-4m4 4 4-4M17 20V4m0 0-4 4m4-4 4 4" />
                </svg>
                {{ $t('ui.tables.sort_by') }}
            </button>
        </div>
        <div v-if="(useMobile || forceMobile) && (activeMobileFilters.length || currentMobileSortHeader)" class="mb-4 flex flex-wrap gap-2">
            <div
                v-for="header in activeMobileFilters"
                :key="header.key"
                class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1.5 text-sm text-gray-700"
            >
                <span class="min-w-0 truncate">{{ header.title || header.value || header.key }}: "{{ formatFilterChipValue(header) }}"</span>
                <button
                    type="button"
                    class="shrink-0 font-semibold text-gray-500 hover:text-gray-800"
                    :aria-label="$t('ui.client_selector_modal.remove_filter') + ' ' + (header.title || header.value || header.key)"
                    @click.stop="removeMobileFilter(header)"
                >
                    ×
                </button>
            </div>
            <div v-if="currentMobileSortHeader" class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                <span class="min-w-0 truncate">
                    {{ $t('ui.tables.sort_by') }}: "{{ currentMobileSortHeader.title || currentMobileSortHeader.value || currentMobileSortHeader.key }}: {{ $t(currentMobileSort.order === 'desc' ? 'ui.tables.descending' : 'ui.tables.ascending') }}"
                </span>
                <button
                    type="button"
                    class="shrink-0 font-semibold text-gray-500 hover:text-gray-800"
                    :aria-label="$t('actions.clear') + ' ' + $t('ui.tables.sort_by')"
                    @click.stop="removeMobileSort"
                >
                    ×
                </button>
            </div>
        </div>

        <v-data-table-server 
            :sort-asc-icon="SortAscIcon" 
            :sort-desc-icon="SortDescIcon"
            :next-icon="NextIcon"
            :prev-icon="PrevIcon"
            :first-icon="FirstIcon"
            :last-icon="LastIcon"
            :mobile="useMobile || forceMobile"
            :density="(useMobile || forceMobile) ? (mobileCompact ? 'compact' : 'default') : 'default'"
            v-on:update:sortBy="reloadFn($event)" 
            v-on:update:itemsPerPage="reloadFn(undefined, $event)"
            v-on:update:page="reloadFn(undefined, undefined, $event)"
            v-bind="$attrs"
            :headers="tableHeaders"
            :class="['rounded-lg shadow-md', { 'pst-mobile-table': useMobile || forceMobile }]"
            return-object
        >
        <template v-slot:body.prepend="{ columns }">
            <tr v-if="columns.find(x => x.filterable) && !useMobile && !forceMobile" class="pst-filter-row">
                <th v-for="column in columns" class="v-data-table__th" :key="column.key">
                    <!-- Callback javascript reference-->
                    <template v-if="column.filterable && column.callback && typeof(column.callback) === 'function'">
                        <div class="relative w-full inline-flex items-center">
                            <span class="clickinput" @click="column.callback"> {{ column.filtervalue == '' ? $t('ui.tables.select_default') : column.filtervalue }}</span>
                            <span class="absolute right-3 flex items-center">
                                <v-icon 
                                    v-if="column.filtervalue !== ''"
                                    name="fa-trash" 
                                    scale="1.1" 
                                    class="cursor-pointer text-red-500 hover:text-red-700"
                                    @click.stop="editHeaderFilter(column.key, '', false)"
                                />
                                <v-icon name="hi-arrows-expand" scale="1.2" class="text-gray-500 cursor-pointer" @click.stop="column.callback"/>
                            </span>
                        </div>
                    </template>
                    
                    
                    <!-- Select/Listbox filter -->
                    <select 
                        v-else-if="column.filterable && column.select" 
                        v-model="column.filtervalue" 
                        @change="editHeaderFilter(column.key, $event.target.value, false)"
                        class="p93__input_general w-32"
                    >
                        <option value="">{{ column.selectPlaceholder || $t('ui.tables.all') }}</option>
                        <option 
                            v-for="option in column.selectOptions" 
                            :key="option.value" 
                            :value="option.value"
                        >
                            {{ option.text }}
                        </option>
                    </select>
                    
                    <!-- Date picker filter -->
                    <VueDatePicker 
                        v-else-if="column.filterable && column.date"
                        v-model="column.filtervalue"
                        @update:model-value="editHeaderFilter(column.key, $event, true)"                     
                        locale="it"
                        format="dd/MM/yyyy"
                        :placeholder="$t('ui.choose_date')"
                        :enable-time-picker="false"
                        class="border rounded-md"
                        :teleport="true"
                        auto-apply
                        text-input
                    />

                    <!-- Boolean filter -->
                    <div v-else-if="column.filterable && column.boolean" class="flex cursor-pointer text-left" @click="toggleBooleanFilter(column)">
                        <v-icon v-if="column.filtervalue === true" name="md-checkbox-round" scale="1.2" fill="green" />
                        <v-icon v-else-if="column.filtervalue === false" name="md-indeterminatecheckbox-round" scale="1.2" fill="red" />
                        <v-icon v-else name="md-checkboxoutlineblank-round" scale="1.2" />
                    </div>
                    
                    <!-- Text input filter -->
                    <input 
                        v-else-if="column.filterable" 
                        v-model="column.filtervalue" 
                        type="text" 
                        :placeholder="$t('ui.tables.filter_input_placeholder')" 
                        class="p93__input_general w-full" 
                        @change="editHeaderFilter(column.key, $event.srcElement.value, false)"
                    />
                </th>
            </tr>
        </template>

        <!-- Header: checkbox di selezione multipla -->
        <template #header.data-table-select="{ allSelected, someSelected, selectAll }">
            <div class="d-flex align-center justify-center" @click="selectAll(!allSelected)">
                <v-icon
                    v-if="allSelected"
                    name="md-checkbox-round"
                    scale="1.2"
                />
                <v-icon
                    v-else-if="someSelected && !allSelected"
                    name="md-indeterminatecheckbox-round"
                    scale="1.2"
                />
                <v-icon
                    v-else-if="!someSelected"
                    name="md-checkboxoutlineblank-round"
                    scale="1.2"
                />
            </div>
        </template>

        <!-- Riga: checkbox di selezione singola -->
        <template #item.data-table-select="{ isSelected, toggleSelect, internalItem }">
            <div class="d-flex align-center justify-center" @click.stop="toggleSelect(internalItem)">
                <v-icon
                    v-if="isSelected(internalItem)"
                    name="md-checkbox-round"
                    scale="1.2"
                />
                <v-icon
                    v-else
                    name="md-checkboxoutlineblank-round"
                    scale="1.2"
                />
            </div>
        </template>

        <!-- Per farli funzionare bisogna passare un header con [ "value" => "_delete" ]-->
        <template #item._delete="{ index }">
            <div class="d-flex align-center justify-center" style="cursor:pointer" @click="deleteElement(index)">
                <v-icon
                    name="md-removecircleoutline-round"
                    scale="1.2"
                    fill="red"
                />
            </div>
        </template>

        <template #header._delete="{ index }">
            <div class="d-flex align-center justify-center" style="cursor:pointer" @click="deleteSelected()" v-if="showDeleteSelected">
                <v-icon
                    name="md-removecircle-round"
                    scale="1.2"
                    fill="red"
                />
            </div>
        </template>

        <!-- Fai passare gli altri template in caso servissero -->
        <template
            v-for="(_, slotName) in $slots"
            v-slot:[slotName]="slotProps"
        >
            <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>

        </v-data-table-server>
    </div>

    <div
        v-if="stickyScrollbar && hasStickyScrollbarOverflow"
        ref="ghostScrollbar"
        class="pst-sticky-scrollbar"
        @scroll.passive="onGhostScroll"
    >
        <div ref="ghostInner" style="height: 1px;"></div>
    </div>

    <!-- Mobile Filter Modal -->
    <div v-if="mobileDialog === 'filters' && (useMobile || forceMobile)" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6 relative max-h-[80vh] flex flex-col">
            <button class="absolute top-2 right-2 text-gray-400 hover:text-gray-600" @click="cancelMobileFilters">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            <h3 class="text-lg font-semibold mb-4">{{ $t('actions.filters') }}</h3>
            <div class="mb-4 space-y-4 overflow-y-auto flex-grow pr-2">
                <div v-for="header in mobileFilterDraftHeaders" :key="header.key" class="mb-4">
                    <label class="block text-sm font-medium mb-1">{{ header.title || header.value }}</label>
                    
                    <!-- Callback javascript reference-->
                    <template v-if="header.callback && typeof(header.callback) === 'function'">
                        <div class="relative w-full inline-flex items-center">
                            <span class="clickinput" @click="header.callback"> {{ header.filtervalue == '' ? $t('ui.tables.select_default') : header.filtervalue }}</span>
                            <span class="absolute right-3 flex items-center">
                                <v-icon 
                                    v-if="header.filtervalue !== ''"
                                    name="fa-trash" 
                                    scale="1.1" 
                                    class="cursor-pointer text-red-500 hover:text-red-700"
                                    @click.stop="updateMobileFilterDraft(header.key, '')"
                                />
                                <v-icon name="hi-arrows-expand" scale="1.2" class="text-gray-500 cursor-pointer" @click.stop="header.callback"/>
                            </span>
                        </div>
                    </template>
                    
                    <!-- Select/Listbox filter -->
                    <select 
                        v-else-if="header.select" 
                        v-model="header.filtervalue" 
                        @change="updateMobileFilterDraft(header.key, $event.target.value)"
                        class="p93__input_general w-full"
                    >
                        <option value="">{{ header.selectPlaceholder || $t('ui.tables.all') }}</option>
                        <option 
                            v-for="option in header.selectOptions" 
                            :key="option.value" 
                            :value="option.value"
                        >
                            {{ option.text }}
                        </option>
                    </select>
                    
                    <!-- Date picker filter -->
                    <VueDatePicker 
                        v-else-if="header.date"
                        v-model="header.filtervalue"
                        @update:model-value="updateMobileFilterDraft(header.key, $event, true)"
                        locale="it"
                        format="dd/MM/yyyy"
                        :placeholder="$t('ui.choose_date')"
                        :enable-time-picker="false"
                        class="border rounded-md"
                        :teleport="true"
                        auto-apply
                        text-input
                    />

                    <!-- Boolean filter -->
                    <div v-else-if="header.filterable && header.boolean" class="flex cursor-pointer text-left" @click="toggleMobileBooleanFilter(header)">
                        <v-icon v-if="header.filtervalue === true" name="md-checkbox-round" scale="1.2" fill="green" />
                        <v-icon v-else-if="header.filtervalue === false" name="md-indeterminatecheckbox-round" scale="1.2" fill="red" />
                        <v-icon v-else name="md-checkboxoutlineblank-round" scale="1.2" />
                    </div>
                    
                    <!-- Text input filter -->
                    <input 
                        v-else
                        v-model="header.filtervalue" 
                        type="text" 
                        :placeholder="$t('gm.filter_by') + ' ' + (header.title || header.value)" 
                        class="p93__input_general w-full" 
                        @change="updateMobileFilterDraft(header.key, $event.target.value)"
                    />
                </div>
            </div>
            <div class="flex justify-end space-x-2">
                <button class="bg-blue-600 text-white px-4 py-2 rounded" @click="applyMobileFilters">{{ $t('actions.apply') }}</button>
            </div>
        </div>
    </div>

    <div v-if="mobileDialog === 'sort' && (useMobile || forceMobile)" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6 relative max-h-[80vh] flex flex-col overflow-hidden">
            <button class="absolute top-2 right-2 text-gray-400 hover:text-gray-600" @click="cancelMobileSort">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            <h3 class="text-lg font-semibold mb-4">{{ $t('ui.tables.sort_by') }}</h3>
            <div role="radiogroup" :aria-label="$t('ui.tables.sort_by')" class="mb-4 max-h-[55vh] min-h-0 flex-none overflow-y-auto overscroll-contain touch-pan-y pr-2">
                <div class="space-y-2">
                    <label
                        v-for="option in mobileSortOptions"
                        :key="option.value"
                        class="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{ 'border-blue-500 bg-blue-50': mobileSortDraft === option.value }"
                    >
                        <input
                            v-model="mobileSortDraft"
                            type="radio"
                            name="pst-mobile-sort-option"
                            :value="option.value"
                            class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                        >
                        <span>{{ option.label }}</span>
                    </label>
                </div>
            </div>
            <div class="flex justify-end space-x-2">
                <button class="bg-blue-600 text-white px-4 py-2 rounded" @click="applyMobileSort">{{ $t('actions.apply') }}</button>
            </div>
        </div>
    </div>

    <ConfirmationModal :show="showDeleteModal" @close="showDeleteModal = false">
        <template #title>
            {{ $t('core.delete_confirmation.title') }}
        </template>

        <template #content>
            {{ $t('core.delete_confirmation.description') }}
        </template>

        <template #footer>
            <SecondaryButton @click="showDeleteModal = false">
                {{ $t('core.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="deleteInternal"
            >
                {{ $t('core.delete') }}
            </DangerButton>
        </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showDeleteSelectedModal" @close="showDeleteSelectedModal = false">
        <template #title>
            {{ $t('core.delete_confirmation.title') }}
        </template>

        <template #content>
            {{ $t('core.delete_confirmation.description') }}
        </template>

        <template #footer>
            <SecondaryButton @click="showDeleteSelectedModal = false">
                {{ $t('core.cancel') }}
            </SecondaryButton>

            <DangerButton
                class="ms-3"
                @click="deleteSelectedInternal"
            >
                {{ $t('core.delete') }}
            </DangerButton>
        </template>
    </ConfirmationModal>
</template>

<style scoped>
.clickinput {
  @apply pl-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-10 cursor-pointer;
}
.pst-sticky-scrollbar {
    position: fixed;
    bottom: 0;
    overflow-x: auto;
    overflow-y: hidden;
    z-index: 50;
    height: 14px;
}
</style>
<style>
.pst-hide-scrollbar::-webkit-scrollbar {
    display: none;
}

.pst-mobile-table .v-data-table__td-sort-select .v-input__control,
.pst-mobile-table .v-data-table__td-sort-select .v-input__prepend,
.pst-mobile-table .v-data-table__td-sort-select .v-input__details {
    display: none;
}

.pst-mobile-table .v-data-table__td-sort-select .v-input__append {
    align-items: center;
    padding-top: 0;
}

.pst-mobile-table .v-table__wrapper > table > thead > tr > th {
    height: 32px;
    padding-top: 0;
    padding-bottom: 0;
}
</style>

<script setup>
import { SortAscIcon, SortDescIcon, NextIcon, PrevIcon, FirstIcon, LastIcon} from "./FunctionalComponents.vue"
import ConfirmationModal from "./ConfirmationModal.vue";
import DangerButton from '@/Components/DangerButton.vue';
import SecondaryButton from "./SecondaryButton.vue";
import { format, formatDistance, parseISO } from 'date-fns';
import { useDisplay } from 'vuetify';
</script>

<script>

export default {
    props: {
        reloadFn: { //Funzione di tipo Fn(sortby = undefined, itemsperpage = undefined, afterdeletion = false)
            type: Function,
            required: true
        },
        deleteTracker: { //Array in cui viene tenuta traccia delle chiavi degli elementi eliminati
            type: Array,
            required: false
        },
        hideDeleteConfirm: { //Ignora i popup di conferma eliminazione
            type: Boolean,
            required: false,
            default: false
        },
        forceMobile: { //Forza la visualizzazione mobile (utile per testing)
            type: Boolean,
            required: false,
            default: false
        },
        mobileCompact: { //Forza la densità compatta in mobile
            type: Boolean,
            required: false,
            default: true
        },
        showMobileFilters: { //Show mobile filter button and modal
            type: Boolean,
            required: false,
            default: true
        },
        mobileFilterExclusions: { //Array of header values to exclude from mobile filters
            type: Array,
            required: false,
            default: () => ['select']
        },
        stickyHeader: {
            type: Boolean,
            required: false,
            default: false
        },
        stickyScrollbar: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ['updated'],
    data() {
        return {
            useMobile: false,
            deleteIndex: -1,
            showDeleteSelected: false,
            showDeleteModal: false,
            showDeleteSelectedModal: false,
            mobileDialog: null,
            mobileFilterDrafts: {},
            mobileSortDraft: '',
            resizeObserver: null,
            hasStickyScrollbarOverflow: false
        };
    },
    watch: {
        mobileDialog(value) {
            document.body.classList.toggle('overflow-hidden', value !== null);
        }
    },
    computed: {
        tableHeaders() {
            const headers = this.$attrs.headers || [];
            if (!(this.useMobile || this.forceMobile)) return headers;
            return headers.map(header => ({ ...header, sortable: false }));
        },
        mobileSortableHeaders() {
            return (this.$attrs.headers || []).filter(header =>
                header.key &&
                header.sortable !== false &&
                header.key !== 'data-table-select' &&
                header.key !== '_delete'
            );
        },
        mobileSortOptions() {
            const options = [{ value: '', label: this.$t('ui.tables.no_sort') }];
            this.mobileSortableHeaders.forEach((header, index) => {
                const title = header.title || header.value || header.key;
                options.push(
                    { value: `${index}:asc`, label: `${title}: ${this.$t('ui.tables.ascending')}` },
                    { value: `${index}:desc`, label: `${title}: ${this.$t('ui.tables.descending')}` }
                );
            });
            return options;
        },
        currentMobileSort() {
            const sortBy = this.$attrs.sortBy ?? this.$attrs['sort-by'] ?? [];
            const currentSort = Array.isArray(sortBy) ? sortBy[0] : null;
            if (typeof currentSort === 'string') return { key: currentSort, order: 'asc' };
            return currentSort;
        },
        currentMobileSortOption() {
            const currentSort = this.currentMobileSort;
            if (!currentSort) return '';
            const index = this.mobileSortableHeaders.findIndex(header => header.key === currentSort.key);
            return index < 0 ? '' : `${index}:${currentSort.order || 'asc'}`;
        },
        currentMobileSortHeader() {
            const currentSort = this.currentMobileSort;
            return currentSort
                ? this.mobileSortableHeaders.find(header => header.key === currentSort.key) || null
                : null;
        },
        mobileFilterHeaders() {
            if (!this.$attrs.headers) return [];
            return this.$attrs.headers.filter(header => 
                !this.mobileFilterExclusions.includes(header.value) &&
                header.filterable !== false
            );
        },
        mobileFilterDraftHeaders() {
            return this.mobileFilterHeaders.map(header => ({
                ...header,
                filtervalue: Object.prototype.hasOwnProperty.call(this.mobileFilterDrafts, header.key)
                    ? this.mobileFilterDrafts[header.key]
                    : header.filtervalue
            }));
        },
        activeMobileFilters() {
            return this.mobileFilterHeaders.filter(header =>
                header.filtervalue !== '' &&
                header.filtervalue !== null &&
                header.filtervalue !== undefined
            );
        }
    },
    methods: {
        openMobileFilters() {
            this.mobileFilterDrafts = {};
            this.mobileFilterHeaders.forEach(header => {
                this.mobileFilterDrafts[header.key] = header.filtervalue instanceof Date
                    ? new Date(header.filtervalue)
                    : header.filtervalue;
            });
            this.mobileDialog = 'filters';
        },
        cancelMobileFilters() {
            this.mobileFilterDrafts = {};
            this.mobileDialog = null;
        },
        updateMobileFilterDraft(key, value, isdate = false) {
            if (isdate && value != null) {
                value = format(new Date(value), 'yyyy-MM-dd');
            }
            this.mobileFilterDrafts = { ...this.mobileFilterDrafts, [key]: value ?? '' };
        },
        toggleMobileBooleanFilter(header) {
            const currentValue = this.mobileFilterDrafts[header.key];
            const value = currentValue === '' || currentValue === undefined
                ? true
                : currentValue === true
                    ? false
                    : '';
            this.updateMobileFilterDraft(header.key, value);
        },
        applyMobileFilters() {
            this.mobileFilterHeaders.forEach(header => {
                header.filtervalue = this.mobileFilterDrafts[header.key] ?? '';
            });
            this.mobileFilterDrafts = {};
            this.mobileDialog = null;
            this.reloadFn();
        },
        formatFilterChipValue(header) {
            const value = header.filtervalue;
            if (header.date && typeof value === 'string') {
                const date = parseISO(value);
                return Number.isNaN(date.getTime()) ? value : format(date, 'dd/MM/yyyy');
            }
            if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? '' : format(value, 'dd/MM/yyyy');
            }
            if (Array.isArray(value)) return value.join(', ');
            if (value && typeof value === 'object') {
                const displayValue = value.label ?? value.text ?? value.value;
                return displayValue === undefined ? JSON.stringify(value) : String(displayValue);
            }
            return String(value);
        },
        removeMobileFilter(header) {
            this.editHeaderFilter(header.key, '', false);
        },
        openMobileSort() {
            this.mobileSortDraft = this.currentMobileSortOption;
            this.mobileDialog = 'sort';
        },
        cancelMobileSort() {
            this.mobileSortDraft = '';
            this.mobileDialog = null;
        },
        applyMobileSort() {
            const sortOption = this.mobileSortDraft;
            this.mobileSortDraft = '';
            this.mobileDialog = null;
            this.applyMobileSortOption(sortOption);
        },
        applyMobileSortOption(value) {
            if (!value) {
                this.reloadFn([]);
                return;
            }

            const [headerIndex, order] = value.split(':');
            const header = this.mobileSortableHeaders[Number(headerIndex)];
            if (!header || !['asc', 'desc'].includes(order)) return;

            this.reloadFn([{ key: header.key, order }]);
        },
        removeMobileSort() {
            this.reloadFn([]);
        },
        deleteElement(index) {
            this.deleteIndex = index;
            if (!this.hideDeleteConfirm) {
                this.showDeleteModal = true;
            }
            else this.deleteInternal();
        },
        deleteInternal(){
            this.showDeleteModal = false;
            var removed = this.$attrs.items.splice(this.deleteIndex, 1);
            if (this.deleteTracker) {
                this.deleteTracker.push(removed[0]);
            }

            this.reloadFn(undefined, undefined, undefined, true);
        },
        deleteSelected() {
            if (!this.hideDeleteConfirm) {
                this.showDeleteSelectedModal = true;
            }
            else this.deleteSelectedInternal();
        },
        deleteSelectedInternal(){
            this.showDeleteSelectedModal = false;

            this.$refs.selected

            //Rimozione in visualizzazione
            this.$attrs.modelValue.forEach(x => {
                var renderremove = this.$attrs.items.filter(y => y == x);
                if (renderremove.length > 0)
                    for (var i=0; i <renderremove.length; i++)
                        this.$attrs.items.splice(this.$attrs.items.indexOf(renderremove[i]), 1);
            })

            var removed = this.$attrs.modelValue.splice(0, this.$attrs.modelValue.length)
            if (this.deleteTracker) {
                this.deleteTracker.push.apply(this.deleteTracker, removed);
            }

            this.reloadFn(undefined, undefined, undefined, true);
        },
        editHeaderFilter(key, value, isdate){
            if (isdate && value != null){
                value = format(new Date(value), 'yyyy-MM-dd');
            }
            this.$attrs.headers.find(x => x.key == key).filtervalue = value ?? '';
            this.reloadFn();
        },
        toggleBooleanFilter(column) {
            if (column.filtervalue === "" || column.filtervalue === undefined) column.filtervalue = true;
            else if (column.filtervalue === true) column.filtervalue = false;
            else column.filtervalue = "";
            this.editHeaderFilter(column.key, column.filtervalue, false);
        },
        _setupStickyScrollbar() {
            const container = this.$refs.container;
            if (!container) return;

            const wrapper = container.querySelector('.v-table__wrapper');
            if (!wrapper) return;

            this._tableWrapper = wrapper;

            this._updateGhostScrollbar = () => {
                const ghost = this.$refs.ghostScrollbar;
                const inner = this.$refs.ghostInner;
                const hasOverflow = wrapper.scrollWidth > wrapper.clientWidth;
                this.hasStickyScrollbarOverflow = hasOverflow;
                wrapper.style.scrollbarWidth = hasOverflow ? 'none' : '';
                wrapper.style.msOverflowStyle = hasOverflow ? 'none' : '';
                wrapper.classList.toggle('pst-hide-scrollbar', hasOverflow);
                if (!ghost || !inner || !hasOverflow) return;

                const rect = wrapper.getBoundingClientRect();
                ghost.style.left = rect.left + 'px';
                ghost.style.width = rect.width + 'px';
                inner.style.width = wrapper.scrollWidth + 'px';
            };

            this._onWrapperScroll = () => {
                if (this._syncingFromGhost) return;
                const ghost = this.$refs.ghostScrollbar;
                if (ghost) {
                    this._syncingFromWrapper = true;
                    ghost.scrollLeft = wrapper.scrollLeft;
                    this._syncingFromWrapper = false;
                }
            };

            wrapper.addEventListener('scroll', this._onWrapperScroll, { passive: true });

            this._ghostResizeObserver = new ResizeObserver(() => this._updateGhostScrollbar());
            this._ghostResizeObserver.observe(wrapper);

            this._ghostWindowHandler = () => this._updateGhostScrollbar();
            window.addEventListener('scroll', this._ghostWindowHandler, { passive: true });
            window.addEventListener('resize', this._ghostWindowHandler, { passive: true });

            this._updateGhostScrollbar();
        },
        onGhostScroll() {
            if (this._syncingFromWrapper) return;
            const ghost = this.$refs.ghostScrollbar;
            if (ghost && this._tableWrapper) {
                this._syncingFromGhost = true;
                this._tableWrapper.scrollLeft = ghost.scrollLeft;
                this._syncingFromGhost = false;
            }
        },
        _updateStickyHeader() {
            const container = this.$refs.container;
            if (!container) return;

            const thead = container.querySelector('thead');
            const filterRow = container.querySelector('tr.pst-filter-row');

            if (!thead) return;

            const tableRect = container.getBoundingClientRect();
            const offset = this._navbarHeight - tableRect.top;

            if (offset > 0 && !(this.useMobile || this.forceMobile)) {
                thead.style.position = 'relative';
                thead.style.top = offset + 'px';
                thead.style.zIndex = '10';
                thead.classList.add('bg-sky-100');

                if (filterRow) {
                    filterRow.style.position = 'relative';
                    filterRow.style.top = offset - 1 + 'px';
                    filterRow.style.zIndex = '9';
                    filterRow.classList.add('bg-sky-100');
                }
            } else {
                thead.style.position = '';
                thead.style.top = '';
                thead.style.zIndex = '';
                thead.classList.remove('bg-sky-100');

                if (filterRow) {
                    filterRow.style.position = '';
                    filterRow.style.top = '';
                    filterRow.style.zIndex = '';
                    filterRow.classList.remove('bg-sky-100');
                }
            }
        }
    },
    mounted()
    {
        if (this.$attrs['show-select'] != undefined){
            this.showDeleteSelected = true;
        }

        const { thresholds } = useDisplay()

        this.resizeObserver = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width
            this.useMobile = width < thresholds.value.sm
        })

        this.resizeObserver.observe(this.$refs.container)

        if (this.stickyHeader) {
            const fixedNav = document.querySelector('.fixed.top-0');
            this._navbarHeight = fixedNav ? fixedNav.getBoundingClientRect().height : 80;
            this._stickyScrollHandler = () => this._updateStickyHeader();
            window.addEventListener('scroll', this._stickyScrollHandler, { passive: true });
        }

        if (this.stickyScrollbar) {
            this.$nextTick(() => this._setupStickyScrollbar());
        }
    },
    beforeUnmount()
    {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this._stickyScrollHandler) {
            window.removeEventListener('scroll', this._stickyScrollHandler);
        }
        if (this._ghostResizeObserver) {
            this._ghostResizeObserver.disconnect();
        }
        if (this._ghostWindowHandler) {
            window.removeEventListener('scroll', this._ghostWindowHandler);
            window.removeEventListener('resize', this._ghostWindowHandler);
        }
        if (this._tableWrapper && this._onWrapperScroll) {
            this._tableWrapper.removeEventListener('scroll', this._onWrapperScroll);
        }
    },
    updated()
    {
        this.$emit('updated');
        if (this.stickyScrollbar && this._updateGhostScrollbar) {
            this.$nextTick(() => this._updateGhostScrollbar());
        }
    }
};
</script>